import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Wrench, Loader2, Search, Building2, Calendar, FileText, User, Phone, Mail, Briefcase,
  DollarSign, Clock, Eye, Receipt, File, ChevronDown, ChevronRight, Plus, CheckCircle, X,
  UserCheck, Users, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BoardServices = () => {
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState({});
  const [approvingHakedis, setApprovingHakedis] = useState(null);

  // Currency symbols
  const currencySymbols = {
    'TRY': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };

  // Status labels and colors
  const statusLabels = {
    'aktif': 'Aktif',
    'tamamlandi': 'Tamamlandı',
    'iptal': 'İptal',
    'beklemede': 'Beklemede'
  };

  const statusColors = {
    'aktif': 'bg-green-100 text-green-700',
    'tamamlandi': 'bg-blue-100 text-blue-700',
    'iptal': 'bg-red-100 text-red-700',
    'beklemede': 'bg-yellow-100 text-yellow-700'
  };

  useEffect(() => {
    loadServices();
    loadBranches();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/purchasing/services`, { headers: getAuthHeaders() });
      setServices(res.data || []);
    } catch (error) {
      console.error('Hizmetler yüklenirken hata:', error);
      toast.error('Hizmetler yüklenemedi');
    }
    setLoading(false);
  };

  const loadBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, { headers: getAuthHeaders() });
      setBranches(res.data || []);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  };

  const openDetailModal = async (service) => {
    try {
      const res = await axios.get(`${API}/purchasing/services/${service.id}`, { headers: getAuthHeaders() });
      setSelectedService(res.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Hizmet detayı yüklenirken hata:', error);
      toast.error('Hizmet detayı yüklenemedi');
    }
  };

  // Toggle expanded row
  const toggleRowExpand = (serviceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Hakediş onaylama (Yönetim Kurulu)
  const handleApproveHakedis = async (serviceId, hakedisId) => {
    setApprovingHakedis(`${hakedisId}-yonetim_kurulu`);
    try {
      await axios.post(
        `${API}/purchasing/services/${serviceId}/hakedis/${hakedisId}/approve`,
        { approval_type: 'yonetim_kurulu' },
        { headers: getAuthHeaders() }
      );
      toast.success('Yönetim Kurulu onayı verildi');
      loadServices(); // Listeyi yenile
    } catch (err) {
      console.error('Onay hatası:', err);
      toast.error('Onay verilemedi');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Hakediş onayını geri alma
  const handleRevokeApproval = async (serviceId, hakedisId) => {
    setApprovingHakedis(`${hakedisId}-yonetim_kurulu-revoke`);
    try {
      await axios.post(
        `${API}/purchasing/services/${serviceId}/hakedis/${hakedisId}/revoke-approval`,
        { approval_type: 'yonetim_kurulu' },
        { headers: getAuthHeaders() }
      );
      toast.success('Onay geri alındı');
      loadServices();
    } catch (err) {
      console.error('Onay geri alma hatası:', err);
      toast.error('Onay geri alınamadı');
    } finally {
      setApprovingHakedis(null);
    }
  };

  const filteredServices = services.filter(service => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = (
      (service.service_name || '').toLowerCase().includes(search) ||
      (service.service_no || '').toLowerCase().includes(search) ||
      (service.cari_hesap_name || '').toLowerCase().includes(search) ||
      (service.description || '').toLowerCase().includes(search)
    );
    
    const matchesBranch = selectedBranch === 'all' || service.branch_id === selectedBranch;
    
    return matchesSearch && matchesBranch;
  });

  // Stats
  const stats = {
    total: services.length,
    bekleyenOnay: services.filter(s => 
      s.hakedisler?.some(h => !h.yonetim_kurulu_onay)
    ).length,
    onaylandi: services.filter(s => 
      s.hakedisler?.every(h => h.yonetim_kurulu_onay)
    ).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="board-services-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-500">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-services-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wrench className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alınan Hizmetler - Hakediş Onayları</h1>
            <p className="text-gray-500 text-sm">Yönetim Kurulu hakediş onay işlemleri</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam Hizmet</p>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
              </div>
              <Wrench className="h-10 w-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Onay Bekleyen</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.bekleyenOnay}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Onaylandı</p>
                <p className="text-2xl font-bold text-green-700">{stats.onaylandi}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Hizmet ara (isim, firma, açıklama...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-services-input"
              />
            </div>
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Şube seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şubeler</SelectItem>
                {branches.map(branch => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-purple-600" />
            Hakediş Listesi ({filteredServices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredServices.length === 0 ? (
            <div className="text-center py-12" data-testid="no-services-message">
              <Wrench className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Henüz hizmet bulunmuyor</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Hizmet No</TableHead>
                    <TableHead>Hizmet Adı</TableHead>
                    <TableHead>Şube</TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Hakediş</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServices.map((service) => (
                    <React.Fragment key={service.id}>
                      {/* Ana satır */}
                      <TableRow 
                        className={`cursor-pointer hover:bg-purple-50 transition-colors ${expandedRows[service.id] ? 'bg-purple-50' : ''}`}
                        onClick={() => toggleRowExpand(service.id)}
                        data-testid={`service-row-${service.id}`}
                      >
                        <TableCell className="w-8">
                          {expandedRows[service.id] ? (
                            <ChevronDown className="h-4 w-4 text-purple-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <Badge variant="outline">{service.service_no || '-'}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {service.service_name || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50">
                            {service.branch_name || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span>{service.cari_hesap_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            {service.hakedisler?.length || 0} adet
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[service.status] || 'bg-gray-100'}>
                            {statusLabels[service.status] || service.status || 'Beklemede'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailModal(service);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expandable içerik - Hakedişler */}
                      {expandedRows[service.id] && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-gray-50 p-0">
                            <div className="p-4">
                              {service.hakedisler && service.hakedisler.length > 0 ? (
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                    <Receipt className="h-4 w-4 text-purple-600" />
                                    Hakedişler ({service.hakedisler.length})
                                  </h4>
                                  <div className="grid gap-3">
                                    {service.hakedisler.map((hakedis, index) => (
                                      <div 
                                        key={hakedis.id || index} 
                                        className="bg-white rounded-lg border p-4 shadow-sm"
                                        data-testid={`hakedis-row-${hakedis.id}`}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                              <span className="font-semibold text-purple-700">
                                                {index + 1}. Hakediş
                                              </span>
                                              <Badge className={
                                                hakedis.durum === 'odendi' ? 'bg-green-100 text-green-700' :
                                                hakedis.durum === 'onaylandi' ? 'bg-blue-100 text-blue-700' :
                                                hakedis.durum === 'iptal' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                              }>
                                                {hakedis.durum === 'odendi' ? 'Ödendi' :
                                                 hakedis.durum === 'onaylandi' ? 'Onaylandı' :
                                                 hakedis.durum === 'iptal' ? 'İptal' : 'Beklemede'}
                                              </Badge>
                                            </div>
                                            <div className="grid grid-cols-4 gap-4 text-sm">
                                              <div>
                                                <span className="text-gray-500">Tarih:</span>
                                                <p className="font-medium">
                                                  {hakedis.tarih ? new Date(hakedis.tarih).toLocaleDateString('tr-TR') : '-'}
                                                </p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Miktar:</span>
                                                <p className="font-medium">{hakedis.miktar || '-'}</p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Tutar:</span>
                                                <p className="font-semibold text-green-600">
                                                  {parseFloat(hakedis.tutar || 0).toLocaleString('tr-TR')} {currencySymbols[service.currency] || '₺'}
                                                </p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Açıklama:</span>
                                                <p className="font-medium">{hakedis.aciklama || '-'}</p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Onay Butonları */}
                                        <div className="mt-4 pt-4 border-t flex items-center gap-6">
                                          {/* Şube Yönetim Onayı (Sadece görüntüleme) */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <UserCheck className="h-4 w-4 text-amber-600" />
                                              <span className="text-sm font-medium text-gray-700">Şube Yönetim</span>
                                            </div>
                                            {hakedis.sube_yonetim_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.sube_yonetim_onay_yapan && `(${hakedis.sube_yonetim_onay_yapan})`}
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-gray-400">Bekliyor</span>
                                            )}
                                          </div>
                                          
                                          {/* Yönetim Kurulu Onayı (Bu panelden onaylanır) */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <Users className="h-4 w-4 text-purple-600" />
                                              <span className="text-sm font-medium text-gray-700">Yönetim Kurulu</span>
                                            </div>
                                            {hakedis.yonetim_kurulu_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.yonetim_kurulu_onay_yapan && `(${hakedis.yonetim_kurulu_onay_yapan})`}
                                                </span>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRevokeApproval(service.id, hakedis.id);
                                                  }}
                                                  disabled={approvingHakedis === `${hakedis.id}-yonetim_kurulu-revoke`}
                                                >
                                                  {approvingHakedis === `${hakedis.id}-yonetim_kurulu-revoke` ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                  ) : (
                                                    <X className="h-3 w-3" />
                                                  )}
                                                </Button>
                                              </div>
                                            ) : (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleApproveHakedis(service.id, hakedis.id);
                                                }}
                                                disabled={approvingHakedis === `${hakedis.id}-yonetim_kurulu`}
                                                data-testid={`approve-yonetim-btn-${hakedis.id}`}
                                              >
                                                {approvingHakedis === `${hakedis.id}-yonetim_kurulu` ? (
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                  <Plus className="h-3 w-3" />
                                                )}
                                                Onayla
                                              </Button>
                                            )}
                                          </div>
                                          
                                          {/* Satın Alma Onayı (Sadece görüntüleme) */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <ShoppingCart className="h-4 w-4 text-green-600" />
                                              <span className="text-sm font-medium text-gray-700">Satın Alma</span>
                                            </div>
                                            {hakedis.satin_alma_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.satin_alma_onay_yapan && `(${hakedis.satin_alma_onay_yapan})`}
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-gray-400">Bekliyor</span>
                                            )}
                                          </div>
                                          
                                          {/* Tam Onay Durumu */}
                                          {hakedis.sube_yonetim_onay && hakedis.yonetim_kurulu_onay && hakedis.satin_alma_onay && (
                                            <Badge className="ml-auto bg-green-100 text-green-700">
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                              Tam Onaylı
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-6 text-gray-500">
                                  <Receipt className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                                  <p>Bu hizmet için henüz hakediş girilmemiş</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-purple-600" />
              Hizmet Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-6 py-4" data-testid="service-detail-content">
              {/* Temel Bilgiler */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Hizmet No</Label>
                  <p className="font-mono font-semibold">{selectedService.service_no || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <Badge className={statusColors[selectedService.status] || 'bg-gray-100'}>
                    {statusLabels[selectedService.status] || selectedService.status || 'Beklemede'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-gray-500">Hizmet Adı</Label>
                <p className="font-semibold">{selectedService.service_name || '-'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <p>{selectedService.branch_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Firma</Label>
                  <p className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    {selectedService.cari_hesap_name || '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Toplam Tutar</Label>
                  <p className="text-xl font-bold text-green-600">
                    {(selectedService.total_amount || 0).toLocaleString('tr-TR')} {currencySymbols[selectedService.currency] || '₺'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Hakediş Sayısı</Label>
                  <p className="text-xl font-bold text-purple-600">
                    {selectedService.hakedisler?.length || 0}
                  </p>
                </div>
              </div>

              {selectedService.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p>{selectedService.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardServices;
